import * as React from 'react'
import { Button, Card, Descriptions, Space, Tag, Typography } from 'antd'
import { CheckCircleOutlined, CloseSquareOutlined } from '@ant-design/icons'
import { submitEditDeposit } from '../../edit/model'
import { path, role } from '../../../../../libs'
import {
  humanDepositRejectionReason,
  transactionRequestStatus,
} from '../../../const'
import { Guard } from '../../../../../features/session/guard'
import { DepositEdit, ClientPurse, InstalmentItem } from '../../edit/types'
import { DepositStatusActions } from './DepositStatusActions'
import { DepositAmountsBlock } from './DepositAmountsBlock'
import { DepositCommissionsBlock } from './DepositCommissionsBlock'
import { DepositPaymentBlock } from './DepositPaymentBlock'
import { DepositVendorData } from './DepositVendorData'
import { DepositComposition } from './DepositComposition'

export type DepositDetailTabProps = {
  deposit: DepositEdit
  loading: boolean
  actionLoading: boolean
  error: { message: string }
  success: { message: string }
  clientPurses: ClientPurse[]
  instalmentItem: InstalmentItem
  instalmentLoading: boolean
  userRole: string[] | undefined
  setIsOpenAdjustmentDepositModal: (open: boolean) => void
}

export const DepositDetailTab = ({
  deposit,
  loading,
  actionLoading,
  error,
  success,
  clientPurses,
  instalmentItem,
  instalmentLoading,
  userRole,
  setIsOpenAdjustmentDepositModal,
}: DepositDetailTabProps) => {
  const isSupport =
    userRole != undefined &&
    (userRole.indexOf(role.clientSupport.supportAgent()) >= 0 ||
      userRole.indexOf(role.clientSupport.supportManager()) >= 0)

  const isNovaSale =
    deposit.transactionType == 'TRANSACTION_REQUEST_TYPE_NOVA_SALE'

  const korapayExtra =
    deposit.processingProvider == 'korapay' &&
    deposit.externalPaymentTransaction?.extraData
      ? Object.entries(JSON.parse(deposit.externalPaymentTransaction.extraData))
      : []

  const hasTransactionDetails =
    korapayExtra.length > 0 ||
    (deposit.processingProvider == 'spayz' && !!deposit.clientPurse) ||
    deposit.rejectReason?.length > 0 ||
    deposit.psComment?.length > 0 ||
    deposit.managerComment?.length > 0 ||
    deposit.internalComment?.length > 0

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Status & actions */}
      <DepositStatusActions
        deposit={deposit}
        userRole={userRole}
        actionLoading={actionLoading}
        setIsOpenAdjustmentDepositModal={setIsOpenAdjustmentDepositModal}
      />

      {/* Meta */}
      <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }} bordered>
        <Descriptions.Item label="ID">
          {deposit.transactionId}
        </Descriptions.Item>
        <Descriptions.Item label="Processing type">
          <Tag color={deposit.isAuto ? '#2eb85c' : '#321fdb'}>
            {deposit.isAuto ? 'Auto' : 'Manual'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Is Card">
          {deposit.isCard ? (
            <CheckCircleOutlined className="text-success" />
          ) : (
            <CloseSquareOutlined className="text-danger" />
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Product">
          <Tag
            color={deposit.product == 'headway' ? '#dbb454' : '#50f9ac'}
            style={{ color: '#000' }}
          >
            {deposit.product}
          </Tag>
        </Descriptions.Item>
        {deposit.changeHistory?.user?.email && (
          <Descriptions.Item label="Manager">
            {deposit.changeHistory.user.email}
          </Descriptions.Item>
        )}
        {deposit.confirmedByManagerAt && (
          <Descriptions.Item label="Confirmed by manager">
            {deposit.confirmedByManagerAt}
          </Descriptions.Item>
        )}
        {deposit.confirmedByVendorAt && (
          <Descriptions.Item label="Confirmed by vendor">
            {deposit.confirmedByVendorAt}
          </Descriptions.Item>
        )}
        {!instalmentLoading && instalmentItem.status != '' && isNovaSale && (
          <Descriptions.Item label="Instalment" span={2}>
            <a
              target="_blank"
              href={path.nova.instalments.entry(
                instalmentItem?.installment?.id,
              )}
            >
              {instalmentItem?.installment?.id}
            </a>{' '}
            · payment {instalmentItem?.planStage}
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Amounts */}
      <DepositAmountsBlock
        deposit={deposit}
        loading={loading}
        error={error}
        success={success}
      />

      {/* Commissions */}
      <DepositCommissionsBlock deposit={deposit} />

      {/* Payment */}
      <DepositPaymentBlock
        deposit={deposit}
        clientPurses={clientPurses}
        userRole={userRole}
      />

      {/* Transaction details */}
      {hasTransactionDetails && (
        <Card size="small" title="Transaction details">
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {korapayExtra.map(([key, value]) => (
              <Typography.Text key={key}>{`${key}: ${value}`}</Typography.Text>
            ))}
            {deposit.processingProvider == 'spayz' && deposit.clientPurse && (
              <>
                <Typography.Text>{`Bank name: ${deposit.clientPurse.bankName}`}</Typography.Text>
                <Typography.Text>{`Client name: ${deposit.clientPurse.clientName}`}</Typography.Text>
              </>
            )}
            {deposit.rejectReason?.length > 0 && (
              <Typography.Text>
                {`Reject reason: ${humanDepositRejectionReason(
                  deposit.rejectReason,
                )}`}
              </Typography.Text>
            )}
            {deposit.psComment?.length > 0 && (
              <Typography.Text>{`psComment: ${deposit.psComment}`}</Typography.Text>
            )}
            {deposit.managerComment?.length > 0 && (
              <Typography.Text>{`managerComment: ${deposit.managerComment}`}</Typography.Text>
            )}
            {deposit.internalComment?.length > 0 && (
              <Typography.Text>{`internalComment: ${deposit.internalComment}`}</Typography.Text>
            )}
          </Space>
        </Card>
      )}

      {/* Vendor data */}
      <DepositVendorData deposit={deposit} />

      {/* Composition */}
      <DepositComposition deposit={deposit} />

      {/* Save */}
      <Guard permission="edit_tranasaction_data">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            loading={loading}
            disabled={
              loading ||
              deposit.status == transactionRequestStatus.rejected() ||
              isSupport
            }
            onClick={() => submitEditDeposit(deposit)}
          >
            Save
          </Button>
        </div>
      </Guard>
    </Space>
  )
}
